import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import BSModal from 'react-bootstrap/Modal';
import { Sparkles } from 'lucide-react';
import Button from '../Button';
import ProgressBar from '../ProgressBar';
import styles from './AiLoaderModal.module.scss';

/* ────────────────────────────────────────────────────────────
 * AiLoaderModal — blocking loading surface for long-running AI
 * (or otherwise slow) work.
 *
 *   <AiLoaderModal open={isGenerating} />
 *
 * Everything has a sensible default, so the one-liner above is a
 * complete usage. Override only what the flow needs:
 *
 *   <AiLoaderModal
 *     open={isGenerating}
 *     title="Generating your report"
 *     messages={['Reading punches…', 'Building the summary…']}
 *     messageInterval={2000}
 *     progress={percent}          // omit for indeterminate
 *     onCancel={abort}
 *   />
 *
 * Behaviour contract:
 *   - Not dismissible by default — the work is still running, so a
 *     backdrop click or Esc does nothing. Pass `dismissible` (with
 *     `onClose`) when the user is genuinely allowed to walk away.
 *   - No header / close affordance: the loader owns its own copy,
 *     and the only exit is the optional cancel button.
 *   - The timer and the message rotation both reset every time the
 *     modal opens, so a second run never inherits the first's clock.
 *   - After `slowAfterSeconds` a reassurance line appears — long AI
 *     calls read as "hung" without one.
 *
 * Why this wraps `react-bootstrap/Modal` directly instead of the
 * `Modal` primitive: `Modal` always renders a header with a close
 * button, which a blocking loader must not offer. Styling is applied
 * through `contentClassName` / `backdropClassName`, which land on the
 * portaled nodes — Bootstrap renders dialog content into
 * `document.body`, so a class on a wrapping div would never reach it.
 *
 * Motion: MOTION.md §2 rule 1 — loading is one of the two allowed
 * always-on animations. All of it drops under
 * `prefers-reduced-motion: reduce`.
 * ──────────────────────────────────────────────────────────── */

export const DEFAULT_AI_MESSAGES = [
  'Warming up the model…',
  'Reading through your data…',
  'Looking for the patterns that matter…',
  'Putting the answer together…',
  'Double-checking the numbers…',
  'Almost there…',
];

const NOOP = () => {};

const formatElapsed = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const AiLoaderModal = ({
  open,
  onClose,
  dismissible,
  title,
  subtitle,
  icon,
  messages,
  messageInterval,
  showTimer,
  timerLabel,
  progress,
  slowAfterSeconds,
  slowMessage,
  cancelLabel,
  onCancel,
  size,
  className,
}) => {
  const [elapsed, setElapsed] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);

  const safeMessages = Array.isArray(messages) && messages.length ? messages : [];
  const messageCount = safeMessages.length;

  // The rotation interval keys off the message *count*, not the array
  // identity — callers almost always pass an inline literal, which would
  // otherwise re-create the interval on every render and freeze the copy
  // on the first line forever.
  const messagesRef = useRef(safeMessages);
  messagesRef.current = safeMessages;

  useEffect(() => {
    if (!open) {
      setElapsed(0);
      setMessageIndex(0);
      return undefined;
    }
    const id = setInterval(() => setElapsed((value) => value + 1), 1000);
    return () => clearInterval(id);
  }, [open]);

  useEffect(() => {
    if (!open || messageCount < 2 || messageInterval <= 0) return undefined;
    const id = setInterval(
      () => setMessageIndex((index) => (index + 1) % messageCount),
      messageInterval
    );
    return () => clearInterval(id);
  }, [open, messageCount, messageInterval]);

  const currentMessage = messagesRef.current[messageIndex % (messageCount || 1)];
  const isDeterminate = typeof progress === 'number';
  const isSlow =
    !!slowMessage && slowAfterSeconds > 0 && elapsed >= slowAfterSeconds;

  return (
    <BSModal
      show={open}
      onHide={dismissible ? onClose || NOOP : NOOP}
      backdrop={dismissible ? true : 'static'}
      keyboard={!!dismissible}
      centered
      size={size}
      contentClassName={[styles.content, className || ''].filter(Boolean).join(' ')}
      backdropClassName={styles.backdrop}
    >
      <BSModal.Body>
        <div
          className={styles.body}
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <span className={styles.orb} aria-hidden="true">
            <span className={styles.orbRing} />
            <span className={styles.orbPulse} />
            <span className={styles.orbCore}>{icon}</span>
          </span>

          {title && <h3 className={styles.title}>{title}</h3>}
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}

          {currentMessage && (
            <p className={styles.message} key={messageIndex}>
              {currentMessage}
            </p>
          )}

          <div className={styles.progress}>
            {isDeterminate ? (
              <ProgressBar value={progress} max={100} showLabel />
            ) : (
              <span className={styles.indeterminate} aria-hidden="true">
                <span className={styles.indeterminateBar} />
              </span>
            )}
          </div>

          {(messageCount > 1 || showTimer) && (
            <div className={styles.meta}>
              {messageCount > 1 && messageCount <= 8 && (
                <span className={styles.dots} aria-hidden="true">
                  {safeMessages.map((message, index) => (
                    <span
                      key={message}
                      className={[
                        styles.dot,
                        index === messageIndex ? styles.dotActive : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    />
                  ))}
                </span>
              )}
              {showTimer && (
                <span className={styles.timer}>
                  {timerLabel && (
                    <span className={styles.timerLabel}>{timerLabel}</span>
                  )}
                  <time className={styles.timerValue}>
                    {formatElapsed(elapsed)}
                  </time>
                </span>
              )}
            </div>
          )}

          {isSlow && <p className={styles.slow}>{slowMessage}</p>}

          {onCancel && (
            <div className={styles.actions}>
              <Button variant="secondary" size="sm" onClick={onCancel}>
                {cancelLabel}
              </Button>
            </div>
          )}
        </div>
      </BSModal.Body>
    </BSModal>
  );
};

AiLoaderModal.propTypes = {
  /** Whether the loader is showing. */
  open: PropTypes.bool,
  /** Only called when `dismissible` is true (backdrop click / Esc). */
  onClose: PropTypes.func,
  /** Lets the user close the loader while the work continues. */
  dismissible: PropTypes.bool,
  /** Headline. Keep it a present-tense statement of the work. */
  title: PropTypes.node,
  /** One short line under the title — set expectations, not detail. */
  subtitle: PropTypes.node,
  /** Icon inside the orb. */
  icon: PropTypes.node,
  /** Rotating status lines. One entry disables the rotation. */
  messages: PropTypes.arrayOf(PropTypes.string),
  /** Milliseconds each message stays up. `0` pins the first message. */
  messageInterval: PropTypes.number,
  /** Show the elapsed-time counter. */
  showTimer: PropTypes.bool,
  /** Label in front of the counter. Pass `''` for the bare clock. */
  timerLabel: PropTypes.node,
  /** 0–100 for a real progress bar; omit for the indeterminate sweep. */
  progress: PropTypes.number,
  /** Seconds before `slowMessage` appears. `0` disables it. */
  slowAfterSeconds: PropTypes.number,
  /** Reassurance shown once the work runs long. */
  slowMessage: PropTypes.node,
  /** Copy for the cancel button (only rendered with `onCancel`). */
  cancelLabel: PropTypes.node,
  /** Providing this renders a cancel button. */
  onCancel: PropTypes.func,
  /** Omit for the app's standard modal width; `sm` for a tighter card. */
  size: PropTypes.oneOf(['sm', 'lg', 'xl']),
  className: PropTypes.string,
};

AiLoaderModal.defaultProps = {
  open: false,
  onClose: undefined,
  dismissible: false,
  title: 'Working on it',
  subtitle: 'This usually takes a few seconds.',
  icon: <Sparkles size={22} strokeWidth={1.75} />,
  messages: DEFAULT_AI_MESSAGES,
  messageInterval: 2600,
  showTimer: true,
  timerLabel: 'Elapsed',
  progress: undefined,
  slowAfterSeconds: 20,
  slowMessage: 'Still going — larger requests can take a little longer.',
  cancelLabel: 'Cancel',
  onCancel: undefined,
  size: undefined,
  className: '',
};

export default AiLoaderModal;
